import {
  ArrowLeftLg,
  ArrowRightLg,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { useMediaGalleryCarousel } from '../../../../contexts/MediaGalleryCarouselContext/MediaGalleryCarouselContext';
import { createScrollOffsetWithinBounds } from './helpers/createScrollOffsetWithinBounds';
import { useOnScrollChange } from './helpers/useOnScrollChange';
import { getMaxScrollOffset } from './helpers/getMaxScrollOffset';
import { isTimelineItemActive } from './helpers/isTimelineItemActive';
import { useScrollActiveYearIntoView } from './helpers/useScrollActiveYearIntoView';

export interface MediaGalleryTimelineData {
  year: string;
  indexTo: number;
  indexFrom: number;
}

const Timeline = styled.div`
  background-color: ${paletteHelper('backgroundTertiaryInvertedStatic')};
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  border-radius: ${radiusHelper('full')};
  overflow: hidden;
  max-width: 300px;
  user-select: none;
  padding: 0 ${spacingHelper('small')};
  pointer-events: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TimelineScrollContainer = styled.div`
  overflow: auto hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
`;

const TimelineItems = styled.ul`
  display: flex;
  pointer-events: auto;
`;

const TimelineItem = styled.li`
  align-items: center;
  display: flex;
  height: 48px;
  position: relative;

  &::after {
    border-bottom: 2px solid ${paletteHelper('textInverted')};
    content: '';
    position: absolute;
    left: ${spacingHelper('extraSmall')};
    bottom: ${spacingHelper('extraSmall')};
    width: calc(100% - ${spacingHelper('medium')});
    transition: opacity 0.4s linear;
    opacity: 0;
  }

  &.active {
    &::after {
      opacity: 1;
    }

    button {
      color: ${paletteHelper('textInverted')};
      border-color: transparent;
    }
  }
`;

const TimelineButton = styled.button`
  border: 2px solid transparent;
  ${typographyHelper('body02Bold')}
  color:${paletteHelper('textSecondary')};
  cursor: pointer;
  outline: none;
  transition: color 0.4s ease;
  height: 26px;
  padding: 0 ${spacingHelper('extraSmall')};
  border-radius: ${radiusHelper('full')};

  @media (hover: hover) {
    &:hover {
      border-color: transparent;
      background: ${paletteHelper('backgroundHoveredTertiaryInvertedStatic')};
    }
  }

  &:focus-visible {
    background: inherit;
    border-color: ${paletteHelper('borderFocusedInvertedStatic')};
  }

  &:active {
    background: ${paletteHelper('backgroundPressedTertiaryInvertedStatic')};
    border-color: transparent;
  }
`;

const TimelineControl = styled.button<{ $control: 'prev' | 'next' }>`
  background: ${paletteHelper('backgroundTertiaryInvertedStatic')};
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: center;
  position: absolute;
  color: ${paletteHelper('textInverted')};
  top: 0;
  height: 48px;
  width: 48px;
  z-index: 1;
  outline: 0;
  pointer-events: auto;
  border: 2px solid transparent;
  border-radius: ${radiusHelper('full')};

  ${({ $control }) =>
    $control === 'prev' &&
    `left: 0;
    mask-image: linear-gradient(90deg, black 75%, transparent 100%);
  `}

  ${({ $control }) =>
    $control === 'next' &&
    `right: 0;
    mask-image: linear-gradient(-90deg, black 75%, transparent 100%);
  `}

  @media (hover: hover) {
    &:hover {
      border-color: transparent;
      background: ${paletteHelper('backgroundHoveredTertiaryInvertedStatic')};
      mask-image: none;
    }
  }

  &:focus-visible {
    background: ${paletteHelper('backgroundTertiaryInvertedStatic')};
    border-color: ${paletteHelper('borderFocusedInvertedStatic')};
    mask-image: none;
  }

  &:active {
    background: ${paletteHelper('backgroundPressedTertiaryInvertedStatic')};
    border-color: transparent;
    mask-image: none;
  }
`;

export const MediaGalleryTimeline: FunctionComponent<{ timeline: MediaGalleryTimelineData[] }> = ({
  timeline,
}) => {
  const { swiper, currentSlide } = useMediaGalleryCarousel();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollElRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);
  const [scrollOffset, setScrollOffset] = useState<number>(0);
  const [suppressScrollEvent, setSuppressScrollEvent] = useState<boolean>(false);
  const [maxScrollOffset, setMaxScrollOffset] = useState<number>(0);

  const showPrevControl = maxScrollOffset ? scrollOffset > 0 : false;
  const showNextControl = maxScrollOffset ? scrollOffset < maxScrollOffset : false;

  //Calculate max scroll offset
  useEffect(() => {
    const offset = getMaxScrollOffset({
      scrollEl: scrollElRef.current,
      scrollContainer: scrollContainerRef.current,
    });
    if (offset) setMaxScrollOffset(offset);
  }, [scrollElRef, scrollContainerRef]);

  //Sync natural scrolling to scrollOffset state
  useOnScrollChange({
    onChange: setScrollOffset,
    scrollContainer: scrollContainerRef.current,
    suppress: suppressScrollEvent,
  });

  //When current slide changes, ensure active year is in view
  useScrollActiveYearIntoView({
    currentSlide,
    timeline,
    scrollContainerRef,
    itemRefs,
    setScrollOffset,
  });

  //Handle prev/next button clicks
  const handleNavigate = (direction: 'prev' | 'next') => {
    if (scrollContainerRef.current === null) return;

    const newScrollOffset = createScrollOffsetWithinBounds({
      currentScrollOffset: scrollOffset,
      maxScrollOffset,
      direction,
    });

    setScrollOffset(newScrollOffset);
    setSuppressScrollEvent(true);

    //Scroll will trigger scroll event, we need to ignore this until scrollTo is complete.
    //Could set a loop to check when it stops if need be,
    //But maybe a simple timeout will do for now.
    setTimeout(() => setSuppressScrollEvent(false), 500);

    scrollContainerRef.current.scrollTo({
      left: newScrollOffset,
      behavior: 'smooth',
    });
  };

  return (
    <Timeline>
      <TimelineScrollContainer ref={scrollContainerRef}>
        {showPrevControl && (
          <TimelineControl $control="prev" onClick={() => handleNavigate('prev')}>
            <ArrowLeftLg />
          </TimelineControl>
        )}

        <TimelineItems ref={scrollElRef}>
          {timeline.map((item, index) => (
            <TimelineItem
              ref={(el) => {
                if (el) itemRefs.current[index] = el;
              }}
              key={item.year}
              className={isTimelineItemActive(item, currentSlide) ? 'active' : ''}
            >
              <TimelineButton onClick={() => swiper?.slideTo(item.indexFrom)}>
                {item.year}
              </TimelineButton>
            </TimelineItem>
          ))}

          {showNextControl && (
            <TimelineControl $control="next" onClick={() => handleNavigate('next')}>
              <ArrowRightLg />
            </TimelineControl>
          )}
        </TimelineItems>
      </TimelineScrollContainer>
    </Timeline>
  );
};
